import {
    SurveyEvaluationRequestDTO,
    SurveySeriesPart,
} from "crqlar-react-common/dist/models/aggregation/surveyAggregation.model";
import {SurveyAggregationService} from "crqlar-react-common/dist/services/aggregation/surveyAggregation.service";
import {useEffect, useRef} from "react";
import {useQuery} from "react-query";
import {SurveyEvaluationFilterState} from "../../shared/SurveyEvaluationFilters";
import {
    SURVEY_BREAKDOWN_QUERY_KEY,
    SURVEY_INTRODUCTION_QUERY_KEY,
    SURVEY_SERIES_QUERY_KEY,
    SURVEY_THEMES_QUERY_KEY,
} from "../../shared/surveyQueryRefresh";
import {buildSurveyEvaluationRequest} from "../../shared/useSurveyEvaluation";
import {InsightsLoading} from "../../../insights/kit/insightsLoading";

/**
 * Query wrapper for survey insights endpoints.
 * Distinguishes between range changes ('refreshing') and survey switches ('skeleton').
 */
function useSurveyInsightsQuery<T>(
    queryKey: string,
    surveyId: string | undefined,
    filters: SurveyEvaluationFilterState,
    fetch: (request: SurveyEvaluationRequestDTO) => Promise<T>,
    keyExtras: unknown[] = []
) {
    const query = useQuery(
        [queryKey, surveyId, filters, ...keyExtras],
        () => fetch(buildSurveyEvaluationRequest(surveyId!, filters)),
        {enabled: !!surveyId, refetchOnWindowFocus: false, keepPreviousData: true}
    );

    const loadedSurveyId = useRef<string | undefined>();

    useEffect(() => {
        if (query.isSuccess && !query.isPreviousData) {
            loadedSurveyId.current = surveyId;
        }
    }, [query.isSuccess, query.isPreviousData, surveyId]);

    const surveyChanged = query.isPreviousData && loadedSurveyId.current !== surveyId;

    const loading: InsightsLoading | undefined = query.isLoading || surveyChanged
        ? 'skeleton'
        : query.isPreviousData
            ? 'refreshing'
            : undefined;

    return {data: query.data, loading};
}

export const useSurveyIntroduction = (surveyId: string | undefined, filters: SurveyEvaluationFilterState) => {
    const {data, loading} = useSurveyInsightsQuery(
        SURVEY_INTRODUCTION_QUERY_KEY,
        surveyId,
        filters,
        request => SurveyAggregationService.getIntroduction(request)
    );

    return {introduction: data, loading};
};

export const useSurveySeries = (
    surveyId: string | undefined,
    filters: SurveyEvaluationFilterState,
    parts: SurveySeriesPart[]
) => {
    const {data, loading} = useSurveyInsightsQuery(
        SURVEY_SERIES_QUERY_KEY,
        surveyId,
        filters,
        request => SurveyAggregationService.getSeries({...request, parts}),
        [parts]
    );

    return {series: data, loading};
};

export const useSurveyThemes = (surveyId: string | undefined, filters: SurveyEvaluationFilterState) => {
    const {data, loading} = useSurveyInsightsQuery(
        SURVEY_THEMES_QUERY_KEY,
        surveyId,
        filters,
        request => SurveyAggregationService.getThemes(request)
    );

    return {themes: data, loading};
};

export const useSurveyBreakdown = (surveyId: string | undefined, filters: SurveyEvaluationFilterState) => {
    const {data, loading} = useSurveyInsightsQuery(
        SURVEY_BREAKDOWN_QUERY_KEY,
        surveyId,
        filters,
        request => SurveyAggregationService.getBreakdown(request)
    );

    return {breakdown: data, loading};
};
